import TextArea from "../../../../../Components/Common/TextArea/TextArea";
import { useFormikContext } from "formik";
import Card from "../Card";
import SectionHeader from "../SectionHeader";
import ActionTable from "../ActionTable";
import Input from "../../../../../Components/Common/Input/Input";
import ErrorMessage from "../../../../../Components/Common/ErrorMessage/ErrorMessage";
import { autoFillClass, prefillProps } from "../../utils/prefilled";
import SearchableSelect from "../../../../../Components/Common/SearchableSelect/SearchableSelect";
import {
  PreconFormValues,
  ActionRow,
  ActionStatus,
} from "../../types/precon.types";
import { CHAR_LIMIT } from "../../../../../utils/Common/Constant";

const SCHEDULE_OPTIONS = [
  { label: "No — not provided", value: "No — not provided" },
  { label: "Yes — attached", value: "Yes — attached" },
];
interface Props {
  prefilledFields: Set<string>;
}

const Step7ScheduleActions = ({ prefilledFields }: Props) => {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } =
    useFormikContext<PreconFormValues>();
  const af = (field: keyof PreconFormValues) =>
    autoFillClass(field, prefilledFields);
  const pf = (field: keyof PreconFormValues) =>
    prefillProps(field, prefilledFields);
  const err = (field: keyof PreconFormValues) =>
    errors[field] as string | undefined;
  const isTouched = (field: keyof PreconFormValues) =>
    touched[field] as boolean | undefined;

  const hasErr = (
    field: keyof PreconFormValues,
    have?: number,
    min?: number,
  ): boolean => {
    const val = String(values[field] ?? "");
    const len = val.length;
    if (have !== undefined && len > have) return true;
    if (min !== undefined && len > 0 && len < min) return true;
    return !!(isTouched(field) && err(field));
  };

  const handleRowChange = (
    index: number,
    key: keyof ActionRow,
    value: string,
  ) => {
    const updated = [...values.actionItems];
    updated[index] = { ...updated[index], [key]: value as ActionStatus };
    setFieldValue("actionItems", updated);
  };

  const handleRemoveRow = (index: number) => {
    setFieldValue(
      "actionItems",
      values.actionItems.filter((_, i) => i !== index),
    );
  };

  const handleAddRow = () => {
    const newRow: ActionRow = {
      id: `new-${Date.now()}`,
      party: "",
      action: "",
      status: "Pending",
    };
    setFieldValue("actionItems", [...values.actionItems, newRow]);
  };

  return (
    <div className="screen active">
      <SectionHeader
        title="Schedule Analysis, Closing Comments & Action Items"
        subtitle="Complete the remaining report sections before preview."
      />

      <Card title="Schedule Analysis" badge="Editable" badgeType="edit">
        <div className="fieldRow c3">
          <div className="field">
            <label>Master Schedule Provided?</label>
            <SearchableSelect
              value={values.masterScheduleProvided}
              options={SCHEDULE_OPTIONS}
              onChange={(val) => setFieldValue("masterScheduleProvided", val)}
              getOptionLabel={(o) => o.label}
              getOptionValue={(o) => o.value}
              disableSearch
            />
          </div>
          <div className="field">
            <label>Typical Duration for This Project Type</label>
            <Input
              name="typicalDuration"
              className={`fi${hasErr("typicalDuration", CHAR_LIMIT.SHORT) ? " error-input-red" : ""}`}
              value={values.typicalDuration}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.SHORT}
            />
            <ErrorMessage
              value={values.typicalDuration}
              have={CHAR_LIMIT.SHORT}
              touched={isTouched("typicalDuration")}
              error={err("typicalDuration")}
            />
          </div>
          <div className="field">
            <label>Stated Duration</label>
            <Input
              name="statedDuration"
              className={`${af("statedDuration")}${hasErr("statedDuration", CHAR_LIMIT.SHORT) ? " error-input-red" : ""}`}
              value={values.statedDuration}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.SHORT}
              {...pf("statedDuration")}
            />
            <ErrorMessage
              value={values.statedDuration}
              have={CHAR_LIMIT.SHORT}
              touched={isTouched("statedDuration")}
              error={err("statedDuration")}
            />
          </div>
        </div>
        <div className="field">
          <label>
            Schedule Narrative <span className="req">*</span>
          </label>
          <TextArea
            name="scheduleNarrative"
            className={`ft textareaTall${hasErr("scheduleNarrative", CHAR_LIMIT.DESCRIPTION) ? " error-input-red" : ""}`}
            value={values.scheduleNarrative}
            onChange={handleChange}
            onBlur={handleBlur}
            maxChars={CHAR_LIMIT.DESCRIPTION}
          />
          <ErrorMessage
            value={values.scheduleNarrative}
            touched={isTouched("scheduleNarrative")}
            error={err("scheduleNarrative")}
          />
        </div>
      </Card>

      <Card
        title="Closing Comments / Summary"
        badge="Editable"
        badgeType="edit"
      >
        <div className="field">
          <label>
            Closing Narrative <span className="req">*</span>
          </label>
          <TextArea
            name="closingNarrative"
            className={`ft textareaL${hasErr("closingNarrative", CHAR_LIMIT.DESCRIPTION) ? " error-input-red" : ""}`}
            value={values.closingNarrative}
            onChange={handleChange}
            onBlur={handleBlur}
            maxChars={CHAR_LIMIT.DESCRIPTION}
          />
          <ErrorMessage
            value={values.closingNarrative}
            touched={isTouched("closingNarrative")}
            error={err("closingNarrative")}
          />
        </div>
      </Card>

      <Card title="Action Items" badge="Editable" badgeType="edit">
        <ActionTable
          rows={values.actionItems}
          onRowChange={handleRowChange}
          onAddRow={handleAddRow}
          onRemoveRow={handleRemoveRow}
          touched={!!touched.actionItems}
          error={err("actionItems")}
          rowErrors={
            Object.keys(touched).some((k) => k.startsWith("actionItems"))
              ? values.actionItems.map((row) =>
                  !row.party.trim()
                    ? "Fill in the party name or remove this row"
                    : undefined,
                )
              : undefined
          }
        />
      </Card>

      <Card
        title="Documents Reviewed / Received"
        badge="Editable"
        badgeType="edit"
      >
        <div className="field">
          <label>
            Documents List <span className="req">*</span>
          </label>
          <TextArea
            name="documentsReviewed"
            className={`ft textareaTall${hasErr("documentsReviewed", CHAR_LIMIT.DESCRIPTION) ? " error-input-red" : ""}`}
            value={values.documentsReviewed}
            onChange={handleChange}
            onBlur={handleBlur}
            maxChars={CHAR_LIMIT.DESCRIPTION}
          />
          <ErrorMessage
            value={values.documentsReviewed}
            touched={isTouched("documentsReviewed")}
            error={err("documentsReviewed")}
          />
        </div>
      </Card>

      <Card title="Bank Draw Requirements" badge="Editable" badgeType="edit">
        <div className="field fieldInline">
          <label>Pre-Construction Requirements</label>
          <TextArea
            name="preconRequirements"
            className={`ft textareaS${hasErr("preconRequirements", CHAR_LIMIT.DESCRIPTION) ? " error-input-red" : ""}`}
            value={values.preconRequirements}
            onChange={handleChange}
            onBlur={handleBlur}
            maxChars={CHAR_LIMIT.DESCRIPTION}
          />
          <ErrorMessage
            value={values.preconRequirements}
            touched={isTouched("preconRequirements")}
            error={err("preconRequirements")}
          />
        </div>
        <div className="field fieldInline">
          <label>Construction Draw Requirements</label>
          <TextArea
            name="drawRequirements"
            className={`ft textareaM${hasErr("drawRequirements", CHAR_LIMIT.DESCRIPTION) ? " error-input-red" : ""}`}
            value={values.drawRequirements}
            onChange={handleChange}
            onBlur={handleBlur}
            maxChars={CHAR_LIMIT.DESCRIPTION}
          />
          <ErrorMessage
            value={values.drawRequirements}
            touched={isTouched("drawRequirements")}
            error={err("drawRequirements")}
          />
        </div>
        <div className="field fieldInline">
          <label>Construction Close-Out Requirements</label>
          <TextArea
            name="closeoutRequirements"
            className={`ft${hasErr("closeoutRequirements", CHAR_LIMIT.DESCRIPTION) ? " error-input-red" : ""}`}
            value={values.closeoutRequirements}
            onChange={handleChange}
            onBlur={handleBlur}
            maxChars={CHAR_LIMIT.DESCRIPTION}
          />
          <ErrorMessage
            value={values.closeoutRequirements}
            touched={isTouched("closeoutRequirements")}
            error={err("closeoutRequirements")}
          />
        </div>
      </Card>
    </div>
  );
};

export default Step7ScheduleActions;
